import React, { useState, useEffect, useCallback } from "react";
import styles from "./FormationList.module.sass";
import cn from "classnames";
import { useNavigate } from "react-router-dom";

import Card from "../../components/Card";
import Form from "../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../components/Icon";

import { getFormationList, getFormationById, deleteFormation, duplicateFormation } from "../../services/formation.service";
import { showSuccess, showError } from "../../services/toast.service";

const FormationList = () => {
  const [search, setSearch] = useState("");
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [selectedFormations, setSelectedFormations] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const navigate = useNavigate();

  // Fonction pour charger les formations
  const loadFormations = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      const options = {
        page: currentPage,
        perPage: 10,
        sort: "-created",
      };

      if (search.trim()) {
        options.filter = `exact_title_of_qualification ~ "${search}" || rncp_code ~ "${search}" || diploma_code ~ "${search}"`;
      }

      const result = await getFormationList(options);

      setFormations(result.items || []);
      setTotalPages(result.totalPages || 1);
      setNoResults(result.items.length === 0 && !!search);
    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Erreur lors du chargement des formations:", err);
        setError("Impossible de charger les formations. Veuillez réessayer.");
        setFormations([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  }, [search, currentPage]);

  // Charger les formations au chargement de la page et lorsque les filtres changent
  useEffect(() => {
    loadFormations();
  }, [loadFormations]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    setCurrentPage(1);
    loadFormations();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFormationSelect = (id) => {
    const formation = formations.find((formation) => formation.id === id);
    if (formation) {
      setSelectedFormation(formation);
      navigate(`/admin/formations/${id}`);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  // Gestion de la sélection des formations
  const handleSelectFormation = (id, isSelected) => {
    if (isSelected) {
      setSelectedFormations([...selectedFormations, id]);
    } else {
      setSelectedFormations(selectedFormations.filter(formationId => formationId !== id));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      // Sélectionner toutes les formations de la page actuelle
      const allIds = formations.map(formation => formation.id);
      setSelectedFormations(allIds);
    } else {
      // Désélectionner toutes les formations
      setSelectedFormations([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedFormations([]);
  };

  // Fonction pour supprimer les formations sélectionnées
  const handleDeleteSelected = async (selectedIds) => {
    if (!selectedIds.length) return;

    try {
      // Supprimer chaque formation sélectionnée
      for (const id of selectedIds) {
        await deleteFormation(id);
      }

      // Rafraîchir la liste des formations
      const timer = setTimeout(() => {
        loadFormations();
      }, 300);

      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression des formations:', error);
      throw error;
    }
  };

  // Fonction pour dupliquer les formations sélectionnées
  const handleDuplicateSelected = async (selectedIds) => {
    if (!selectedIds.length) return;

    try {
      // Récupérer et dupliquer chaque formation sélectionnée
      for (const id of selectedIds) {
        // Récupérer les données complètes de la formation
        const formation = await getFormationById(id);

        // Dupliquer la formation
        await duplicateFormation(formation);
      }

      // Rafraîchir la liste des formations
      const timer = setTimeout(() => {
        loadFormations();
      }, 300);

      return true;
    } catch (error) {
      console.error('Erreur lors de la duplication des formations:', error);
      throw error;
    }
  };

  return (
    <>
      <Card
        className={styles.card}
        title="Formations"
        classTitle={cn("title-yellow", styles.title)}
        classCardHead={styles.head}
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={handleSearchChange}
              onSubmit={handleSubmit}
              placeholder="Rechercher par titre, code RNCP ou code diplôme"
              type="text"
              name="search"
              icon="search"
            />
            <button className={cn("button-stroke button-small", styles.button)} onClick={() => navigate("/admin/formations/create")}>Créer</button>
          </>
        }
      >
        {error && <div className={styles.error}>{error}</div>}
        
        {loading ? (
          <div className={styles.loading}>Chargement des formations...</div>
        ) : (
          <div className={styles.row}>
            {noResults && formations.length === 0 && (
              <div className={styles.empty}>
                <p>Aucune formation trouvée pour votre recherche "{search}".</p>
              </div>
            )}
            {!noResults && formations.length === 0 && !loading && !error && (
              <div className={styles.empty}>
                <p>Aucune formation disponible.</p>
              </div>
            )}
            {formations.length > 0 && (
              <>
                <Table
                  className={styles.table}
                  items={formations}
                  onFormationSelect={handleFormationSelect}
                  selectedFormations={selectedFormations}
                  onSelectFormation={handleSelectFormation}
                  onSelectAll={handleSelectAll}
                />
                
                <div className={styles.foot}>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
                    disabled={currentPage === 1}
                  >
                    <Icon name="arrow-left" size="20" />
                  </button>
                  <span className={styles.pagination}>
                    Page {currentPage} sur {totalPages}
                  </span>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
                    disabled={currentPage === totalPages}
                  >
                    <Icon name="arrow-right" size="20" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
      <Panel
        selectedFormations={selectedFormations}
        onDeleteSelected={handleDeleteSelected}
        onDuplicateSelected={handleDuplicateSelected}
        onClearSelection={handleClearSelection}
      />
    </>
  );
};

export default FormationList;